import React, { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router';
import { useDispatch } from 'react-redux';

// Hooks
import { useWorkspaceSnapshotTracking } from 'Shared/tracking/use-workspace-snapshot-tracking';

// Utils
import { formatDate } from 'Shared/utils/DateTime.utils';

// Types
import { WorkspaceMeeting } from 'Shared/types/WorkspaceMeeting.type';

// Hooks
import { useToggle, useMembers } from 'Shared/utils/hooks';

// Actions
import { fetchWorkspaceMeetings } from 'Shared/actions/WorkspaceMeetings.actions';

// Components
import WorkspaceMeetingsSnapshotFilter from './partials/WorkspaceMeetingsSnapshotFilter';
import WorkspaceMeetingSnapshotItem from './partials/WorkspaceMeetingSnapshotItem';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';
import WorkspaceTabHeader from '../../partials/WorkspaceTabHeader';

// Styles
import './WorkspaceAgendaHistory.scss';

const WorkspaceAgendaHistory = () => {
  const { trackViewedEmptyState } = useWorkspaceSnapshotTracking();

  const { workspaceId } = useParams();
  const dispatch = useDispatch();

  const { isLoading: isLoadingMembers } = useMembers();

  const [selectedDay, setSelectedDay] = useState<Date>(null);
  const [isLoading, toggleIsLoading] = useToggle(true);
  const [isLoadingSnapshots, toggleIsLoadingSnapshots] = useToggle(false);
  const [availableWorkspaceMeetings, setAvailableWorkspaceMeetings] = useState<WorkspaceMeeting[]>([]);
  const [snapshotData, setSnapshotData] = useState([]);
  const [visitedMonths, setVisitedMonths] = useState([]);

  const currentMonth = visitedMonths[0];

  const workspaceMeetingsToRender =
    selectedDay &&
    availableWorkspaceMeetings?.length > 0 &&
    availableWorkspaceMeetings
      ?.filter(meeting => formatDate(meeting.startTime, 'YYYY-MM-DD') === formatDate(selectedDay, 'YYYY-MM-DD'))
      .sort((a, b) => {
        const meetingA = a.startTime;
        const meetingB = b.startTime;
        return meetingA > meetingB ? -1 : meetingA < meetingB ? 1 : 0;
      });

  const showEmpty = !isLoading && availableWorkspaceMeetings?.length === 0;

  const handleSetSelectedMonth = useCallback(
    date => {
      const month = formatDate(date, 'YYYY-MM') + '-01';
      setVisitedMonths([month, ...visitedMonths]);
    },
    [currentMonth]
  );

  useEffect(() => {
    if (!selectedDay) return;
    const date = formatDate(selectedDay, 'YYYY-MM-DD');
    if (snapshotData.find(s => s.filterDate === date)) return;
    toggleIsLoadingSnapshots();

    dispatch(
      fetchWorkspaceMeetings({ workspaceId, params: { view: 'day', date: date, include_snapshots: true } })
    ).then(response => {
      const formattedSnapshots = Object.values(response.workspaceAgendaSnapshot).map(snapshot => {
        // @ts-ignore
        return { ...snapshot.attributes, filterDate: date };
      });

      setSnapshotData([...snapshotData, ...formattedSnapshots]);
      toggleIsLoadingSnapshots();
    });
  }, [selectedDay]);

  useEffect(() => {
    if (!currentMonth) return;
    if (visitedMonths.filter(m => m === currentMonth).length > 1) return;

    dispatch(fetchWorkspaceMeetings({ workspaceId, params: { view: 'month', date: currentMonth } })).then(response => {
      const formatted = Object.values(response.workspaceMeeting)
        // @ts-ignore
        .map(meeting => meeting.attributes);

      setAvailableWorkspaceMeetings([...availableWorkspaceMeetings, ...formatted]);
    });
  }, [currentMonth, visitedMonths]);

  useEffect(() => {
    dispatch(fetchWorkspaceMeetings({ workspaceId, params: { view: 'day' } })).then(response => {
      // @ts-ignore
      const formatted = Object.values(response.workspaceMeeting).map(meeting => meeting.attributes);
      if (formatted.length > 0) {
        const date = new Date(formatted[0].startTime);
        setSelectedDay(date);
        handleSetSelectedMonth(date);
        setAvailableWorkspaceMeetings(formatted);
      }
      toggleIsLoading();
    });
  }, []);

  useEffect(() => {
    if (showEmpty) trackViewedEmptyState();
  }, [showEmpty]);

  if (isLoading || isLoadingMembers) return <>Loading...</>;

  const baseClassName = 'WorkspaceAgendaHistory';

  return (
    <div className={baseClassName}>
      {showEmpty ? (
        <>
          <div className={`${baseClassName}__utility-bar`}>
            <WorkspaceTabHeader title="Meeting Notes" />
          </div>
          <EmptyStateContainer
            subTitle={
              <>
                You don’t have any past meeting agendas yet. Use&nbsp;
                <a
                  href="https://docs.predictiveindex.com/perform/during-meetings/meeting-mode/what-is-meeting-mode/"
                  target="blank"
                  rel="noopener norefferer"
                >
                  meeting mode
                </a>
                &nbsp;during meetings to get the most out of meeting history.
              </>
            }
            inContainer
          />
        </>
      ) : (
        <div>
          <div className={`${baseClassName}__utility-bar`}>
            <WorkspaceTabHeader title="Meeting Notes" />
            <WorkspaceMeetingsSnapshotFilter
              availableWorkspaceMeetings={availableWorkspaceMeetings}
              setSelectedDay={setSelectedDay}
              setSelectedMonth={handleSetSelectedMonth}
              selectedDay={selectedDay}
            />
          </div>
          {isLoadingSnapshots ? (
            <>Loading...</>
          ) : (
            <>
              {workspaceMeetingsToRender?.map(meeting => (
                <div className={`${baseClassName}__snapshot`} key={meeting.slug}>
                  <WorkspaceMeetingSnapshotItem
                    meeting={meeting}
                    snapshot={snapshotData.find(s => s.slug === meeting.snapshotDataSlug)}
                  />
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkspaceAgendaHistory;
